import React, { useState, useMemo } from 'react';
import { Search, MessageCircle, User, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Input } from '../ui/input';

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString || 'N/A';
  }
};

/**
 * StatusBadge component for message status
 * @param {Object} props - Component props
 * @param {number} props.statusCode - HTTP status code
 * @returns {JSX.Element} StatusBadge component
 */
const StatusBadge = ({ statusCode }) => {
  if (statusCode === 200) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
        <CheckCircle2 className="w-3 h-3" />
        Delivered
      </span>
    );
  }

  if (statusCode === null || statusCode === undefined) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
        <AlertCircle className="w-3 h-3" />
        Pending
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
      <XCircle className="w-3 h-3" />
      Failed ({statusCode})
    </span>
  );
};

/**
 * CommunicationCard component for individual message
 * @param {Object} props - Component props
 * @param {Object} props.communication - Communication data
 * @returns {JSX.Element} CommunicationCard component
 */
const CommunicationCard = ({ communication }) => {
  const {
    type,
    templateName,
    components,
    phoneNumber,
    statusCode,
    messageId,
    createdAt,
    quantity,
    bookingId
  } = communication;

  return (
    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase">{type || 'user'}</span>
          <span className="text-sm font-medium text-gray-900">{templateName || 'Unknown Template'}</span>
        </div>
        <StatusBadge statusCode={statusCode} />
      </div>

      {/* Message Content */}
      {components && components.length > 0 && (
        <div className="bg-white rounded p-2 mb-2 border border-gray-200">
          {components.map((component, idx) => (
            component.type === 'text' && (
              <p key={idx} className="text-sm text-gray-700">
                {component.text}
              </p>
            )
          ))}
        </div>
      )}

      {/* Metadata */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="space-x-3">
          <span>Phone: {phoneNumber || 'N/A'}</span>
          {bookingId && <span>Booking: {bookingId}</span>}
          {quantity && <span>Qty: {quantity}</span>}
        </div>
        <span>{formatDate(createdAt)}</span>
      </div>

      {/* Message ID (truncated) */}
      {messageId && (
        <p className="text-xs text-gray-400 mt-1 truncate">
          ID: {messageId.substring(0, 30)}...
        </p>
      )}
    </div>
  );
};

/**
 * ClientNotificationGroup component for grouped notifications
 * @param {Object} props - Component props
 * @param {Object} props.group - Client notification group
 * @returns {JSX.Element} ClientNotificationGroup component
 */
const ClientNotificationGroup = ({ group }) => {
  const { clientId, clientName, clientEmail, clientPhone, communications } = group;

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Client Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <User className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-left">
            <h4 className="font-medium text-gray-900">{clientName || 'Unknown Client'}</h4>
            <div className="text-xs text-gray-500 space-x-2">
              <span>ID: {clientId}</span>
              {clientEmail && <span>• {clientEmail}</span>}
              {clientPhone && <span>• {clientPhone}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
            {communications.length} message{communications.length !== 1 ? 's' : ''}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Communications */}
      {isExpanded && (
        <div className="p-4 space-y-3">
          {communications.map((communication, idx) => (
            <CommunicationCard key={idx} communication={communication} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * WhatsAppNotificationsTab component for displaying WhatsApp notifications
 * @param {Object} props - Component props
 * @param {Array} props.notifications - Array of notification groups
 * @returns {JSX.Element} WhatsAppNotificationsTab component
 */
const WhatsAppNotificationsTab = ({ notifications }) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter notifications by clientId, client name, email, or phone
  const filteredNotifications = useMemo(() => {
    if (!searchTerm.trim()) return notifications;
    const term = searchTerm.toLowerCase();
    return notifications.filter(
      (group) =>
        (group.clientId && group.clientId.toString().includes(term)) ||
        (group.clientName && group.clientName.toLowerCase().includes(term)) ||
        (group.clientEmail && group.clientEmail.toLowerCase().includes(term)) ||
        (group.clientPhone && group.clientPhone.includes(term))
    );
  }, [notifications, searchTerm]);

  // Calculate total messages
  const totalMessages = useMemo(() => {
    return filteredNotifications.reduce((acc, group) => acc + group.communications.length, 0);
  }, [filteredNotifications]);

  if (!notifications || notifications.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <MessageCircle className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600 text-lg">No WhatsApp notifications</p>
        <p className="text-gray-500 text-sm mt-1">This agent has no WhatsApp communication history</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by client ID, name, email, or phone..."
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="text-sm text-gray-500">
          {filteredNotifications.length} client{filteredNotifications.length !== 1 ? 's' : ''} • {totalMessages} message{totalMessages !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Search className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600">No clients match your search</p>
          </div>
        ) : (
          filteredNotifications.map((group, index) => (
            <ClientNotificationGroup
              key={`${group.clientId || index}`}
              group={group}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default WhatsAppNotificationsTab;
